import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchDiffReport } from '@/utils/graphqlClient';
import { DiffEventLightbox } from '@/components/diffs/DiffEventLightbox';
import { EditableTrendCard } from '@/components/diffs/EditableTrendCard';
import { SaveConfirmationDialog } from '@/components/diffs/SaveConfirmationDialog';
import { PublishConfirmationDialog } from '@/components/diffs/PublishConfirmationDialog';
import { UnpublishConfirmationDialog } from '@/components/diffs/UnpublishConfirmationDialog';
import { supabase } from '@/integrations/supabase/client';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  Edit3,
  Save,
  X as CloseIcon,
  Copy,
  XCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from "@/hooks/use-toast";
import {
  cloneReportContent,
  validateReportContent,
  uploadWithRetry,
  updateReportUrls,
  calculateMetadata,
  generateDraftUrl,
  generatePublishedUrl,
  uploadReportToStorage
} from '@/utils/diffReportUtils';
import type { Report, JsonReportContent, Trend } from '@/types';

// Types are now imported from @/types

// Helper function to format period into readable date range
const formatPeriodDateRange = (period: string): string => {
  const quarterMatch = period.match(/(\d{4})Q(\d)/);
  if (quarterMatch) {
    const year = parseInt(quarterMatch[1]);
    const quarter = parseInt(quarterMatch[2]);

    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];

    const startMonth = (quarter - 1) * 3;
    const endMonth = quarter * 3 - 1;

    const startMonthName = months[startMonth];
    const endMonthName = months[endMonth];

    // Calculate last day of end month
    const lastDay = new Date(year, endMonth + 1, 0).getDate();
    const lastDayOrdinal = lastDay + (lastDay === 1 ? 'st' : lastDay === 2 ? 'nd' : lastDay === 3 ? 'rd' : 'th');

    return `${startMonthName} 1, ${year} → ${endMonthName} ${lastDayOrdinal}, ${year}`;
  }

  // For weekly periods, just return the period as is
  return period;
};

export default function DiffReport() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [report, setReport] = useState<Report | null>(null);
  const [jsonContent, setJsonContent] = useState<JsonReportContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingJson, setLoadingJson] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force data refresh

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>();
  const [selectedCompanySlug, setSelectedCompanySlug] = useState<string>('');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<JsonReportContent | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Track specific edits
  const [deletedTrendIndices, setDeletedTrendIndices] = useState<Set<number>>(new Set());
  const [deletedExampleIds, setDeletedExampleIds] = useState<Set<string>>(new Set());
  const [editedTrends, setEditedTrends] = useState<Map<number, Partial<Trend>>>(new Map());

  // Save/publish state
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);

  // Handler to refresh event/asset data from DB
  const handleRefreshData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Load report data directly from database
  useEffect(() => {
    const loadReport = async () => {
      if (!reportId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Query database directly instead of using GraphQL
        const { data: dbReport, error } = await supabase
          .from('app_diff_reports')
          .select('*')
          .eq('id', reportId)
          .single();

        if (error) throw error;
        if (!dbReport) throw new Error('Report not found');

        // Generate a title from the period if not available
        const title = dbReport.period.includes('W')
          ? `Weekly Pricing Changes - ${dbReport.period}`
          : `Quarterly Pricing Analysis - ${dbReport.period}`;

        // Map database fields to Report interface
        const mappedReport: Report = {
          id: dbReport.id,
          title,
          period: dbReport.period,
          status: 'completed', // Reports in DB are already completed
          created_at: dbReport.created_at,
          updated_at: dbReport.updated_at,
          summary: `Analysis of pricing changes${dbReport.companies_count ? ` across ${dbReport.companies_count} companies` : ''}`,
          markdown_url: dbReport.markdown_url,
          json_url: dbReport.json_url,
          draft_url: dbReport.draft_url,
          html_url: dbReport.html_url,
          published: dbReport.published || false,
          published_url: dbReport.published_url,
          published_at: dbReport.published_at,
          insights: dbReport.analysis_metadata?.insights || [],
          stats: {
            totalDiffs: dbReport.diffs_count || 0,
            totalChanges: dbReport.events_count || 0,
            companies: dbReport.companies_count || 0,
          },
        };

        setReport(mappedReport);
        setLoading(false);

        // Enable auto-refresh if report is in progress
        if (mappedReport.status === 'pending' || mappedReport.status === 'running') {
          setAutoRefresh(true);
        }
      } catch (error) {
        console.error('Error loading report:', error);
        toast({
          title: "Error",
          description: "Failed to load report",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId, toast]);

  // Fetch JSON content and events with assets
  useEffect(() => {
    const loadJsonContent = async () => {
      // Prefer draft_url (newer versioned drafts) over json_url (backward compatibility)
      const contentUrl = report?.draft_url || report?.json_url;
      if (!contentUrl) return;

      setLoadingJson(true);
      try {
        // Fetch JSON report
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch JSON content');
        }
        const content: JsonReportContent = await response.json();

        // Get all unique slugs from trends (more reliable than IDs which may be stale)
        const uniqueSlugs = Array.from(
          new Set(
            content.trends
              .flatMap(t => t.examples)
              .map(e => e.slug)
              .filter(Boolean)
          )
        );

        // Fetch events with assets directly from Supabase by slug
        // Get the most recent event for each company
        const { data: eventsData, error: eventsError } = await supabase
          .from('app_events')
          .select(`
            id,
            slug,
            event_type,
            description,
            asset_id,
            created_at,
            v_before,
            v_after,
            verified
          `)
          .in('slug', uniqueSlugs)
          .order('created_at', { ascending: false });

        if (eventsError) {
          console.error('[DiffReport] Error fetching events:', eventsError);
          throw new Error('Failed to fetch events');
        }

        // Create slug to events lookup FIRST (get first/most recent event per slug)
        // This way we only need to fetch assets for the events we'll actually use
        const slugToEventMap = new Map<string, any>();
        eventsData?.forEach(event => {
          if (!slugToEventMap.has(event.slug)) {
            slugToEventMap.set(event.slug, event);
          }
        });

        // Get unique asset IDs only from the events we'll use (one per slug)
        const assetIds = [...new Set(
          Array.from(slugToEventMap.values())
            .map(e => e.asset_id)
            .filter(Boolean)
        )];

        // Fetch assets in batches if needed (Supabase has limits on IN clause)
        let assetsData: any[] = [];
        const batchSize = 50;
        for (let i = 0; i < assetIds.length; i += batchSize) {
          const batchIds = assetIds.slice(i, i + batchSize);
          const { data: batchData, error: batchError } = await supabase
            .from('app_assets')
            .select(`
              id,
              primary_original_url,
              primary_cropped_url,
              primary_markup_url,
              secondary_original_url,
              secondary_cropped_url,
              secondary_markup_url
            `)
            .in('id', batchIds);

          if (batchError) {
            console.error('[DiffReport] Error fetching assets batch:', batchError);
          } else if (batchData) {
            assetsData = assetsData.concat(batchData);
          }
        }

        // Create asset lookup map
        const assetMap = new Map(assetsData.map(a => [a.id, a]));

        // Update slugToEventMap with asset data
        slugToEventMap.forEach((event, slug) => {
          if (event.asset_id && assetMap.has(event.asset_id)) {
            slugToEventMap.set(slug, {
              ...event,
              asset: assetMap.get(event.asset_id)
            });
          }
        });

        // Enrich examples with event data by matching slug
        const enrichedContent = {
          ...content,
          trends: content.trends.map(trend => ({
            ...trend,
            examples: trend.examples.map(ex => {
              const matchedEvent = slugToEventMap.get(ex.slug);
              return {
                ...ex,
                // Use matched event data if available, keeping original JSON data as fallback
                id: matchedEvent?.id || ex.id,
                asset_id: matchedEvent?.asset_id || ex.asset_id,
                asset: matchedEvent?.asset || null,
                v_before: matchedEvent?.v_before,
                v_after: matchedEvent?.v_after,
                verified: matchedEvent?.verified ?? false,
              };
            }),
          })),
        };

        setJsonContent(enrichedContent as any);

        // Calculate accurate stats from JSON content
        const uniqueCompanies = new Set(
          content.trends.flatMap(trend =>
            trend.examples.map(ex => ex.slug)
          )
        );

        // Update stats with accurate counts
        if (report) {
          setReport({
            ...report,
            stats: {
              companies: uniqueCompanies.size,
              totalDiffs: uniqueCompanies.size,
              totalChanges: content.metadata?.eventCount || content.trends.flatMap(t => t.examples).length,
            },
          });
        }
      } catch (error) {
        console.error('Error loading JSON content:', error);
        toast({
          title: "Warning",
          description: "Could not load detailed trends data",
          variant: "default",
        });
      } finally {
        setLoadingJson(false);
      }
    };

    loadJsonContent();
  }, [report?.draft_url, report?.json_url, toast, refreshKey]);

  // Auto-refresh for in-progress reports
  useEffect(() => {
    if (!autoRefresh || !report) return;

    const interval = setInterval(() => {
      // Reload report data
      // In real implementation, this would fetch updated status
      if (report.status === 'completed' || report.status === 'failed') {
        setAutoRefresh(false);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, report]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { color: string; icon: React.ReactNode }> = {
      completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> },
      running: { color: 'bg-blue-100 text-blue-800', icon: <RefreshCw className="h-3 w-3 animate-spin" /> },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
      failed: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="h-3 w-3" /> },
    };

    const variant = variants[status] || variants.pending;
    return (
      <Badge variant="outline" className={`${variant.color} gap-1`}>
        {variant.icon}
        {status}
      </Badge>
    );
  };

  // Enter edit mode - creates deep copy of JSON content
  const enterEditMode = useCallback(() => {
    if (!jsonContent) {
      toast({
        title: "Cannot Edit",
        description: "Report content is not loaded yet",
        variant: "destructive"
      });
      return;
    }

    // Warn if editing a published report
    if (report?.published) {
      toast({
        title: "Editing Published Report",
        description: "Changes will automatically update the published version when saved",
      });
    }

    setEditedContent(cloneReportContent(jsonContent));
    setIsEditing(true);
    setIsDirty(false);
    setDeletedTrendIndices(new Set());
    setDeletedExampleIds(new Set());
    setEditedTrends(new Map());
  }, [jsonContent, report, toast]);

  // Exit edit mode with confirmation if dirty
  const exitEditMode = useCallback(() => {
    if (isDirty) {
      const confirm = window.confirm(
        'You have unsaved changes. Are you sure you want to exit edit mode? All changes will be lost.'
      );
      if (!confirm) return;
    }

    setIsEditing(false);
    setEditedContent(null);
    setIsDirty(false);
    setDeletedTrendIndices(new Set());
    setDeletedExampleIds(new Set());
    setEditedTrends(new Map());
  }, [isDirty]);

  // Handler for save button click - shows confirmation dialog
  const handleSaveClick = useCallback(() => {
    if (!isDirty) {
      toast({
        title: "No Changes",
        description: "There are no changes to save",
        variant: "default"
      });
      return;
    }
    setShowSaveDialog(true);
  }, [isDirty, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to cancel edit mode
      if (e.key === 'Escape' && isEditing) {
        e.preventDefault();
        exitEditMode();
      }

      // Cmd/Ctrl+S to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && isEditing) {
        e.preventDefault();
        handleSaveClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, exitEditMode, toast, handleSaveClick]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && isEditing) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires this
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isEditing]);

  // Handler for editing trend fields (name or description)
  const handleEditTrend = useCallback((trendIndex: number, field: 'name' | 'description', value: string) => {
    if (!editedContent) return;

    const updatedContent = { ...editedContent };
    updatedContent.trends = [...editedContent.trends];
    updatedContent.trends[trendIndex] = {
      ...updatedContent.trends[trendIndex],
      [field]: value
    };

    setEditedContent(updatedContent);
    setIsDirty(true);

    // Track this edit
    const currentEdits = new Map(editedTrends);
    const existingEdits = currentEdits.get(trendIndex) || {};
    currentEdits.set(trendIndex, { ...existingEdits, [field]: value });
    setEditedTrends(currentEdits);
  }, [editedContent, editedTrends]);

  // Handler for deleting a trend
  const handleDeleteTrend = useCallback((trendIndex: number) => {
    setDeletedTrendIndices(prev => new Set(prev).add(trendIndex));
    setIsDirty(true);
  }, []);

  // Handler for deleting an example
  const handleDeleteExample = useCallback((trendIndex: number, exampleId: string) => {
    setDeletedExampleIds(prev => new Set(prev).add(exampleId));
    setIsDirty(true);
  }, []);

  // Get filtered trends (excluding deleted) with filtered examples (excluding deleted)
  const getFilteredTrends = useMemo(() => {
    if (!editedContent) return [];

    return editedContent.trends
      .map((trend, index) => ({
        ...trend,
        index,
        isDeleted: deletedTrendIndices.has(index),
        examples: trend.examples.filter(ex => !deletedExampleIds.has(ex.id))
      }))
      .filter(trend => !trend.isDeleted && trend.examples.length > 0);
  }, [editedContent, deletedTrendIndices, deletedExampleIds]);

  // Calculate changes for save dialog
  const saveChanges = useMemo(() => {
    return {
      trendsDeleted: deletedTrendIndices.size,
      trendsEdited: editedTrends.size,
      examplesDeleted: deletedExampleIds.size
    };
  }, [deletedTrendIndices, editedTrends, deletedExampleIds]);

  // Actual save implementation
  const handleSaveChanges = useCallback(async () => {
    if (!report || !editedContent) return;

    setIsSaving(true);
    setShowSaveDialog(false);

    try {
      // 1. Apply deletions to create final content
      const finalTrends = editedContent.trends
        .filter((_, index) => !deletedTrendIndices.has(index))
        .map(trend => ({
          ...trend,
          examples: trend.examples.filter(ex => !deletedExampleIds.has(ex.id))
        }))
        .filter(trend => trend.examples.length > 0); // Remove trends with no examples

      // 2. Validate
      const finalContent = {
        ...editedContent,
        trends: finalTrends
      };

      const validation = validateReportContent(finalContent);
      if (!validation.valid) {
        toast({
          title: "Validation Error",
          description: validation.errors[0],
          variant: "destructive"
        });
        setIsSaving(false);
        return;
      }

      // 3. Recalculate metadata
      const updatedMetadata = calculateMetadata(finalTrends, finalContent.metadata);
      const contentToSave = {
        ...finalContent,
        metadata: updatedMetadata
      };

      // 4. Generate new draft URL (versioned) - use json_url for legacy support
      const currentUrl = report.draft_url || report.json_url || '';
      const currentVersion = currentUrl.match(/_v(\d+)_/)?.[1] || '1';
      const nextVersion = parseInt(currentVersion) + 1;
      const newDraftPath = generateDraftUrl(report.period, nextVersion);

      // 5. Upload to storage with retry
      const newDraftUrl = await uploadWithRetry(contentToSave, newDraftPath);

      // 6. Update database with new draft URL
      await updateReportUrls(report.id, {
        json_url: newDraftUrl,  // Keep for backward compatibility
        draft_url: newDraftUrl,
        companies_count: updatedMetadata.companiesCount,
        trends_count: updatedMetadata.trendsCount,
        events_count: updatedMetadata.eventCount
      });

      // 7. Update report state immediately with new URLs
      const updatedReportState = {
        ...report,
        json_url: newDraftUrl,  // Use full URL
        draft_url: newDraftUrl,  // Use full URL
        updated_at: new Date().toISOString(),
        companies_count: updatedMetadata.companiesCount,
        trends_count: updatedMetadata.trendsCount,
        events_count: updatedMetadata.eventCount
      };
      setReport(updatedReportState);

      // 8. Update JSON content and exit edit mode
      setJsonContent(contentToSave);
      setEditedContent(null);
      setIsEditing(false);
      setIsDirty(false);
      setDeletedTrendIndices(new Set());
      setDeletedExampleIds(new Set());
      setEditedTrends(new Map());

      // 9. If report was published, automatically republish with new content
      if (report.published && report.published_url) {
        try {
          const publishedPath = generatePublishedUrl(report.period);

          // Upload updated content to published location
          await uploadReportToStorage(contentToSave, publishedPath);

          // Get the full public URL
          const { data: urlData } = supabase.storage
            .from('reports')
            .getPublicUrl(publishedPath);

          const publicUrl = urlData?.publicUrl;

          if (publicUrl) {
            // Update database with new publish timestamp
            await updateReportUrls(report.id, {
              published_url: publicUrl,
              published_at: new Date().toISOString()
            });

            // Update local state
            setReport({
              ...updatedReportState,
              published_url: publicUrl,
              published_at: new Date().toISOString()
            });

            toast({
              title: "Published",
              description: "Draft saved and published report updated",
            });
          }
        } catch (publishError) {
          console.error('[handleSaveChanges] Republish error:', publishError);
          toast({
            title: "Draft Saved",
            description: "Draft saved but failed to update published version",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Success",
          description: "Report saved successfully",
        });
      }

    } catch (error) {
      console.error('[handleSaveChanges] Error:', error);
      toast({
        title: "Error Saving Report",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  }, [report, editedContent, deletedTrendIndices, deletedExampleIds, toast]);

  const handlePublishClick = () => {
    setShowPublishDialog(true);
  };

  const handlePublish = useCallback(async () => {
    if (!report) return;

    setIsPublishing(true);
    try {
      // 1. Fetch the latest draft content from storage
      const draftUrl = report.draft_url || report.json_url;

      if (!draftUrl) {
        throw new Error('No draft URL available');
      }

      const response = await fetch(draftUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch latest draft');
      }
      const latestDraftContent: JsonReportContent = await response.json();

      // 2. Check if there's already a published report for this period and unpublish it
      const { data: existingPublished, error: checkError } = await supabase
        .from('app_diff_reports')
        .select('id, published_url')
        .eq('period', report.period)
        .eq('published', true)
        .neq('id', report.id)
        .maybeSingle();

      if (checkError) {
        console.error('[handlePublish] Error checking existing published reports:', checkError);
      }

      if (existingPublished) {
        console.log('[handlePublish] Unpublishing existing report:', existingPublished.id);

        // Unpublish the existing report
        await updateReportUrls(existingPublished.id, {
          published: false,
          published_url: null,
          published_at: null
        });

        toast({
          title: "Previous Report Unpublished",
          description: "Only one report can be published per period",
        });
      }

      // 3. Generate consistent published URL
      const publishedPath = generatePublishedUrl(report.period);

      // 4. Upload latest draft content to published location
      await uploadReportToStorage(latestDraftContent, publishedPath);

      // 5. Get the full public URL from Supabase
      const { data: urlData } = supabase.storage
        .from('reports')
        .getPublicUrl(publishedPath);

      const publicUrl = urlData?.publicUrl;

      if (!publicUrl) {
        throw new Error('Could not get public URL');
      }

      // 6. Update database with publishing metadata (store full URL)
      await updateReportUrls(report.id, {
        published: true,
        published_url: publicUrl,
        published_at: new Date().toISOString()
      });

      // 7. Reload report to reflect published state
      const { data: updatedReport, error: reloadError } = await supabase
        .from('app_diff_reports')
        .select('*')
        .eq('id', report.id)
        .single();

      if (!reloadError && updatedReport) {
        setReport({
          ...report,
          published: updatedReport.published || false,
          published_url: updatedReport.published_url,
          published_at: updatedReport.published_at,
        });
      }

      setShowPublishDialog(false);

      toast({
        title: "Report Published",
        description: "Report uploaded to public location",
      });

      console.log('✅ Report published to:', publicUrl);

    } catch (error) {
      console.error('[handlePublish] Error:', error);
      toast({
        title: "Publishing Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsPublishing(false);
    }
  }, [report, toast]);

  const handleCopyPublicLink = useCallback(async () => {
    if (!report?.published_url) return;

    try {
      // published_url is now stored as full URL in database
      await navigator.clipboard.writeText(report.published_url);

      toast({
        title: "Link Copied",
        description: "Public URL copied to clipboard",
      });
    } catch (error) {
      console.error('[handleCopyPublicLink] Error:', error);
      toast({
        title: "Copy Failed",
        description: "Could not copy link to clipboard",
        variant: "destructive"
      });
    }
  }, [report, toast]);


  const handleUnpublishClick = () => {
    setShowUnpublishDialog(true);
  };

  const handleUnpublish = useCallback(async () => {
    if (!report) return;

    setIsUnpublishing(true);
    try {
      // 1. Delete published file from storage if it exists
      if (report.published_url) {
        try {
          const { error: deleteError } = await supabase.storage
            .from('reports')
            .remove([report.published_url]);

          if (deleteError) {
            console.warn('[handleUnpublish] Could not delete published file:', deleteError);
          }
        } catch (storageError) {
          console.warn('[handleUnpublish] Storage deletion error:', storageError);
        }
      }

      // 2. Update database to clear publishing fields (gracefully handle missing columns)
      try {
        await updateReportUrls(report.id, {
          published: false,
          published_url: null as any,
          published_at: null as any
        });
      } catch (dbError) {
        console.warn('[handleUnpublish] Could not update DB (migration may not be applied):', dbError);
      }

      // 3. Update local state
      setReport({
        ...report,
        published: false,
        published_url: undefined,
        published_at: undefined
      });

      setShowUnpublishDialog(false);

      toast({
        title: "Report Unpublished",
        description: "Report removed from public access",
      });

    } catch (error) {
      console.error('[handleUnpublish] Error:', error);
      toast({
        title: "Unpublish Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsUnpublishing(false);
    }
  }, [report, toast]);

  const handleOpenLightbox = (eventId: string, companySlug: string) => {
    setSelectedEventId(eventId);
    setSelectedCompanySlug(companySlug);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedEventId(undefined);
    setSelectedCompanySlug('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-gray-600">Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-8 py-8">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-600" />
              <p className="text-red-800">Report not found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate('/diffs?tab=reports')}
              >
                Back to Reports
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show pending state if report hasn't been processed yet
  if (!report.draft_url && !report.json_url) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-8 py-8">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-8 text-center">
              <RefreshCw className="h-12 w-12 mx-auto mb-3 text-blue-600 animate-spin" />
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Report Generation In Progress
              </h3>
              <p className="text-blue-800 mb-4">
                This report is currently being analyzed. Check back in a few minutes.
              </p>
              {report.task_id && (
                <Button
                  variant="outline"
                  className="mb-4"
                  asChild
                >
                  <Link to={`/tasks/${report.task_id}`}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Task Progress
                  </Link>
                </Button>
              )}
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/diffs?tab=reports')}
                >
                  Back to Reports
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="mb-4"
          >
            <Link to="/diffs?tab=reports" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Reports
            </Link>
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{report.title}</h1>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline">{report.period}</Badge>
                {getStatusBadge(report.status)}
                {report.published && (
                  <Badge variant="outline" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Published
                  </Badge>
                )}
                {isEditing && (
                  <Badge variant="outline" className="bg-orange-100 text-orange-800">
                    <Edit3 className="h-3 w-3 mr-1" />
                    Editing Mode
                  </Badge>
                )}
                {isDirty && isEditing && (
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                    Unsaved Changes
                  </Badge>
                )}
                <span className="text-sm text-gray-500">
                  Created {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                </span>
                {autoRefresh && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    Auto-refreshing...
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Edit mode buttons */}
              {isEditing ? (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSaveClick}
                    disabled={isSaving || !isDirty}
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exitEditMode}
                    disabled={isSaving}
                  >
                    <CloseIcon className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {report.status === 'completed' && jsonContent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={enterEditMode}
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Report
                    </Button>
                  )}
                  {report.task_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <Link to={`/tasks/${report.task_id}`}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Task
                      </Link>
                    </Button>
                  )}
                  {report.status === 'completed' && !report.published && (report.draft_url || report.json_url) && (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a
                        href={`https://pricingsaas.com/news/weekly/preview?json=${encodeURIComponent(report.draft_url || report.json_url || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Preview
                      </a>
                    </Button>
                  )}
                  {report.status === 'completed' && jsonContent && !report.published && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handlePublishClick}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Publish Report
                    </Button>
                  )}
                  {report.published && report.published_url && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyPublicLink}
                        title="Copy Public Link"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUnpublishClick}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Unpublish
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        {report.summary && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{report.summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Status-specific content */}
        {report.status === 'pending' && (
          <Card className="bg-yellow-50 border-yellow-200 mb-6">
            <CardContent className="p-6 text-center">
              <Clock className="h-12 w-12 mx-auto mb-3 text-yellow-600" />
              <p className="text-sm text-yellow-800 font-medium mb-2">
                Report is queued for generation
              </p>
              <p className="text-xs text-yellow-700">
                This page will automatically refresh when the report is ready
              </p>
            </CardContent>
          </Card>
        )}

        {report.status === 'running' && (
          <Card className="bg-blue-50 border-blue-200 mb-6">
            <CardContent className="p-6 text-center">
              <RefreshCw className="h-12 w-12 mx-auto mb-3 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-800 font-medium mb-2">
                Generating report...
              </p>
              <p className="text-xs text-blue-700">
                This page will automatically refresh when complete
              </p>
            </CardContent>
          </Card>
        )}

        {report.status === 'failed' && (
          <Card className="bg-red-50 border-red-200 mb-6">
            <CardContent className="p-6 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-600" />
              <p className="text-sm text-red-800 font-medium mb-2">
                Report generation failed
              </p>
              {report.task_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  asChild
                >
                  <Link to={`/tasks/${report.task_id}`}>
                    View Error Details
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Report Content - only show if completed */}
        {report.status === 'completed' && (
          <>
            {/* Trends with Examples */}
            {loadingJson && (
              <Card className="mb-6">
                <CardContent className="p-8 text-center">
                  <RefreshCw className="h-8 w-8 mx-auto mb-2 text-gray-400 animate-spin" />
                  <p className="text-sm text-gray-600">Loading detailed trends...</p>
                </CardContent>
              </Card>
            )}

            {/* Trends Section - switches between view and edit mode */}
            {(isEditing ? getFilteredTrends : jsonContent?.trends)?.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3">Pricing Trends</h2>
                <div className="space-y-4">
                  {isEditing ? (
                    /* Edit mode - use EditableTrendCard */
                    getFilteredTrends.map((trend) => (
                      <EditableTrendCard
                        key={trend.index}
                        trend={trend}
                        trendIndex={trend.index}
                        isEditing={true}
                        isDeleted={trend.isDeleted}
                        onEdit={handleEditTrend}
                        onDelete={handleDeleteTrend}
                        editedExamples={trend.examples}
                        onDeleteExample={handleDeleteExample}
                        onOpenLightbox={handleOpenLightbox}
                      />
                    ))
                  ) : (
                    /* View mode - use read-only rendering */
                    jsonContent?.trends.map((trend, index) => (
                      <Collapsible key={index} defaultOpen={true}>
                        <Card>
                          <CollapsibleTrigger className="w-full">
                            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-base text-left">{trend.name}</CardTitle>
                                <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200 data-[state=open]:rotate-180" />
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent>
                              <p className="text-gray-700 mb-2">{trend.description}</p>
                              {trend.summary && (
                                <p className="text-sm text-gray-600 italic mb-4">{trend.summary}</p>
                              )}

                              {/* Examples */}
                              {trend.examples && trend.examples.length > 0 && (
                                <div className="mt-4">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Examples:</h4>
                                  <div className="space-y-2">
                                    {trend.examples.map((example, exIdx) => (
                                      <div
                                        key={example.id}
                                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                        onClick={() => handleOpenLightbox(example.id, example.slug)}
                                      >
                                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium">
                                          {exIdx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-blue-600">
                                              {example.slug}
                                            </span>
                                            {example.relevanceScore && (
                                              <span className="text-xs text-gray-500">
                                                ({example.relevanceScore.toFixed(1)}/10)
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-sm text-gray-700">{example.hook}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Strategic Insights */}
            {report.insights && report.insights.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Strategic Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {report.insights.map((insight, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <h3 className="font-semibold text-gray-900 mb-2">{insight.title}</h3>
                        <p className="text-gray-700 text-sm">{insight.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Event Lightbox */}
        {jsonContent && (
          <DiffEventLightbox
            isOpen={lightboxOpen}
            onClose={handleCloseLightbox}
            diffId={report?.id || ''}
            initialEventId={selectedEventId}
            companySlug={selectedCompanySlug}
            onRefresh={handleRefreshData}
            events={jsonContent.trends.flatMap(trend =>
              trend.examples.map(example => ({
                id: example.id,
                event_type: undefined,
                slug: example.slug,
                description: example.hook,
                asset_id: example.asset_id,
                asset: example.asset || null,
                created_at: example.created_at,
                trendName: trend.name,
                v_before: example.v_before,
                v_after: example.v_after,
                verified: example.verified,
                // Flatten asset URLs for lightbox compatibility
                primary_original_url: example.asset?.primary_original_url,
                primary_cropped_url: example.asset?.primary_cropped_url,
                primary_markup_url: example.asset?.primary_markup_url,
                secondary_original_url: example.asset?.secondary_original_url,
                secondary_cropped_url: example.asset?.secondary_cropped_url,
                secondary_markup_url: example.asset?.secondary_markup_url,
              }))
            )}
          />
        )}

        {/* Save Confirmation Dialog */}
        <SaveConfirmationDialog
          open={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          onConfirm={handleSaveChanges}
          isSaving={isSaving}
          changes={saveChanges}
        />

        {/* Publish Confirmation Dialog */}
        <PublishConfirmationDialog
          open={showPublishDialog}
          onClose={() => setShowPublishDialog(false)}
          onConfirm={handlePublish}
          isPublishing={isPublishing}
          period={report.period}
          content={jsonContent}
        />

        {/* Unpublish Confirmation Dialog */}
        <UnpublishConfirmationDialog
          open={showUnpublishDialog}
          onClose={() => setShowUnpublishDialog(false)}
          onConfirm={handleUnpublish}
          isUnpublishing={isUnpublishing}
        />
      </div>
    </div>
  );
}
